import { type Control, Controller, type FieldErrors, type UseFormRegister } from 'react-hook-form';

import type { InvoiceDataSchemaType } from '@/components/steps/InvoicingStep/invoiceDataSchema';
import Input from '@/components/ui/Input';
import SelectInput from '@/components/ui/SelectInput';
import { useCountryCodesPhone } from '@/hooks/useCountryCodesPhone';
import { useLanguageNames } from '@/hooks/useLanguageNames';
import { t } from '@/lib/utils';

type ContactSectionProps = {
    register: UseFormRegister<InvoiceDataSchemaType>;
    control: Control<InvoiceDataSchemaType>;
    errors: FieldErrors<InvoiceDataSchemaType>;
};

const ContactSection = ({ register, control, errors }: ContactSectionProps) => {
    const countryCodes = useCountryCodesPhone;
    const languageNames = useLanguageNames;

    return (
        <div className='margin-bottom-15 margin-top-5 rounded bg-white padding-25'>
            <div className='text-bold text-size-18 padding-bottom-15'>{t('onboarding.contactDetails.title')}</div>
            <div className='padding-bottom-15'>{t('onboarding.contactDetails.description')}</div>

            <div className='display-flex gap-15 margin-bottom-15'>
                <Input
                    required
                    className='flex-1-1-0'
                    label={t('general.personalEmailAddress')}
                    error={errors?.contact?.personalEmailAddress?.message}
                    inputAttributes={{ ...register('contact.personalEmailAddress') }}
                />
                <Controller
                    name='contact.languageOfCommunication'
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <SelectInput
                            required
                            className='flex-1-1-0'
                            error={errors.contact?.languageOfCommunication?.message}
                            label={t('general.languageOfCommunication')}
                            selectAttributes={{
                                onChange: (item: any) => onChange(item.id),
                                options: languageNames(value),
                            }}
                        />
                    )}
                />
            </div>
            <div className='display-flex gap-15'>
                <Controller
                    name='contact.countryCode'
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <div className='display-flex flex-1-1-0 flex-row gap-15'>
                            <SelectInput
                                required
                                className='min-width-auto margin-bottom-0'
                                error={errors.contact?.countryCode?.message}
                                label={t('general.countryCode')}
                                selectAttributes={{
                                    onChange: (item: any) => onChange(item.id),
                                    options: countryCodes(value),
                                }}
                            />
                            <Input
                                required
                                className='flex-1-1-0'
                                label={t('general.contactPhoneNumber')}
                                error={errors?.contact?.phoneNumber?.message}
                                inputAttributes={{ ...register('contact.phoneNumber') }}
                            />
                        </div>
                    )}
                />
                <div className='flex-1-0' />
            </div>
        </div>
    );
};

export default ContactSection;
